export type VulnerabilityPopulation = string;
export type VulnerabilityRange = string;

/** One repeatable row: arbitrary keys from catalog.fields. */
export type VulnerabilityRow = {
  [key: string]: string | null | undefined;
  population?: string;
  other_text?: string | null;
  range?: string;
};

export const VULNERABILITY_DRIVER_KEY = 'vulnerabilidad_involucramiento';
export const VULNERABILITY_ROWS_KEY = 'vulnerabilidad_filas';
export const ROWS_CATALOG_ACTION = 'rows_catalog';
/** form_attributes.input for the dedicated repeatable-rows field type. */
export const REPEATABLE_ROWS_INPUT = 'rows';

/** Slug of «Otros grupos en situación de vulnerabilidad» (same as slugifyRowsCode). */
export const OTHER_POPULATION_VALUE = 'otros-grupos-en-situacion-de-vulnerabilidad';

/** Legacy form attribute keys hidden after migration (form 1). */
export const LEGACY_VULNERABILITY_ATTR_KEYS = new Set([
  '3545541d-b56e-4089-a695-456eed5ef4e0',
  '4abd07ca-ad7d-460c-935c-1074f6810fa8',
  'a7731376-2525-4d90-953b-ed12cb98cf9f',
  'f59d9463-bb07-4f9e-a1d0-b58fd57952d7',
  '725dba94-4e3c-443a-891f-1020f040e5eb',
  '44eaa5b3-68b7-42fa-9fcd-7251443a934a',
  'c22ce380-f1f5-4c55-9e4a-e2c9e3665728',
  '3a600158-7d91-4bbf-ac0d-58b177ac747a',
  'c80f31d1-de52-4437-8ef6-8ccb812b6d52',
  'bf186d92-95ba-4fd8-ba0c-e663a6d5721e'
]);

export interface RowsCatalogOption {
  value: string;
  label: string;
  label_en?: string | null;
  label_fr?: string | null;
  labelKey?: string;
}

export type RepeatableFieldType = 'select' | 'text';

export interface RepeatableFieldShowWhen {
  field: string;
  equals: string;
}

/** Configurable column inside each repeatable row. */
export interface RepeatableFieldDef {
  key: string;
  label: string;
  label_en?: string | null;
  label_fr?: string | null;
  type: RepeatableFieldType;
  required?: boolean;
  options?: RowsCatalogOption[];
  default_value?: string | null;
  /** Options cannot repeat across rows (e.g. population). */
  unique_across_rows?: boolean;
  /** Show this field only when another field equals a value. */
  show_when?: RepeatableFieldShowWhen | null;
}

export interface RepeatableRowsCatalog {
  version: 2;
  max_rows: number;
  fields: RepeatableFieldDef[];
  /** Legacy mirrors kept for SMS / charts / older UI helpers. */
  populations: RowsCatalogOption[];
  ranges: RowsCatalogOption[];
  default_range: string;
  require_other_text_for: string[];
  range_field_label?: string;
  range_field_label_en?: string;
  range_field_label_fr?: string;
}

const DEFAULT_POPULATIONS: RowsCatalogOption[] = [
  { value: 'lgbtiq', label: 'LGBTIQ+', label_en: 'LGBTIQ+', label_fr: 'LGBTIQ+' },
  {
    value: 'mujeres-y-ninas',
    label: 'Mujeres y niñas',
    label_en: 'Women and girls',
    label_fr: 'Femmes et filles'
  },
  {
    value: 'ninos-y-adolescentes',
    label: 'Niños, niñas y adolescentes',
    label_en: 'Children and adolescents',
    label_fr: 'Enfants et adolescents'
  },
  {
    value: 'personas-adultas-mayores',
    label: 'Personas adultas mayores',
    label_en: 'Older adults',
    label_fr: 'Personnes âgées'
  },
  {
    value: 'pueblos-indigenas-y-afrodescendientes',
    label: 'Pueblos indígenas y afrodescendientes',
    label_en: 'Indigenous and Afro-descendant peoples',
    label_fr: 'Peuples autochtones et afrodescendants'
  },
  {
    value: 'personas-migrantes-y-refugiadas',
    label: 'Personas migrantes y refugiadas',
    label_en: 'Migrants and refugees',
    label_fr: 'Migrants et réfugiés'
  },
  {
    value: 'personas-con-discapacidad',
    label: 'Personas con discapacidad',
    label_en: 'Persons with disabilities',
    label_fr: 'Personnes handicapées'
  },
  {
    value: OTHER_POPULATION_VALUE,
    label: 'Otros grupos en situación de vulnerabilidad',
    label_en: 'Other groups in a situation of vulnerability',
    label_fr: 'Autres groupes en situation de vulnérabilité'
  }
];

const DEFAULT_RANGES: RowsCatalogOption[] = [
  {
    value: '1-5',
    label: 'De 1 a 5 personas',
    label_en: 'From 1 to 5 people',
    label_fr: 'De 1 à 5 personnes'
  },
  {
    value: '6-20',
    label: 'De 6 a 20 personas',
    label_en: 'From 6 to 20 people',
    label_fr: 'De 6 à 20 personnes'
  },
  {
    value: '21-50',
    label: 'De 21 a 50 personas',
    label_en: 'From 21 to 50 people',
    label_fr: 'De 21 à 50 personnes'
  },
  {
    value: '51-100',
    label: 'De 51 a 100 personas',
    label_en: 'From 51 to 100 people',
    label_fr: 'De 51 à 100 personnes'
  },
  {
    value: 'mas-de-100',
    label: 'Más de 100 personas',
    label_en: 'More than 100 people',
    label_fr: 'Plus de 100 personnes'
  },
  {
    value: 'no-determinado',
    label: 'No determinado',
    label_en: 'Undetermined',
    label_fr: 'Non déterminé'
  }
];

export function buildDefaultVulnerabilityFields(
  populations: RowsCatalogOption[] = DEFAULT_POPULATIONS,
  ranges: RowsCatalogOption[] = DEFAULT_RANGES,
  opts?: {
    rangeLabel?: string;
    rangeLabelEn?: string;
    rangeLabelFr?: string;
    defaultRange?: string;
    otherEquals?: string;
  }
): RepeatableFieldDef[] {
  const otherEquals = opts?.otherEquals || OTHER_POPULATION_VALUE;
  const defaultRange = opts?.defaultRange || 'no-determinado';
  return [
    {
      key: 'population',
      label: 'Población',
      label_en: 'Population',
      label_fr: 'Population',
      type: 'select',
      required: true,
      unique_across_rows: true,
      options: populations.map(o => ({ ...o }))
    },
    {
      key: 'other_text',
      label: 'Especifique',
      label_en: 'Specify',
      label_fr: 'Précisez',
      type: 'text',
      required: true,
      show_when: { field: 'population', equals: otherEquals }
    },
    {
      key: 'range',
      label:
        opts?.rangeLabel ||
        'Número de poblaciones en situación de vulnerabilidad?',
      label_en:
        opts?.rangeLabelEn ||
        'Number of populations in a situation of vulnerability?',
      label_fr:
        opts?.rangeLabelFr ||
        'Nombre de populations en situation de vulnérabilité ?',
      type: 'select',
      required: true,
      default_value: defaultRange,
      options: ranges.map(o => ({ ...o }))
    }
  ];
}

/** Built-in defaults (used when attribute has no rows_catalog yet). */
export const DEFAULT_ROWS_CATALOG: RepeatableRowsCatalog = normalizeRowsCatalog({
  version: 2,
  max_rows: 8,
  fields: buildDefaultVulnerabilityFields()
});

/** @deprecated Prefer resolveRowsCatalog(...).populations */
export const VULNERABILITY_POPULATIONS = DEFAULT_ROWS_CATALOG.populations.map(p => ({
  value: p.value,
  labelKey: p.label
}));

/** @deprecated Prefer resolveRowsCatalog(...).ranges */
export const VULNERABILITY_RANGES = DEFAULT_ROWS_CATALOG.ranges.map(r => ({
  value: r.value,
  labelKey: r.label
}));

export const VULNERABILITY_MAX_ROWS = DEFAULT_ROWS_CATALOG.max_rows;

export function hasRowsCatalog(config?: any[] | null): boolean {
  return !!(config || []).find(
    (c: any) => c?.action === ROWS_CATALOG_ACTION || c?.action === 'rows_catalog'
  );
}

export function isRepeatableRowsAttribute(fa: {
  key?: string;
  input?: string;
  config?: any[] | null;
}): boolean {
  if (fa?.input === REPEATABLE_ROWS_INPUT) {
    return true;
  }
  return hasRowsCatalog(fa?.config);
}

export function resolveRowsCatalog(config?: any[] | null): RepeatableRowsCatalog {
  const entry = (config || []).find(
    (c: any) => c?.action === ROWS_CATALOG_ACTION || c?.action === 'rows_catalog'
  );
  if (entry?.catalog && typeof entry.catalog === 'object') {
    return normalizeRowsCatalog(entry.catalog);
  }
  return normalizeRowsCatalog(DEFAULT_ROWS_CATALOG);
}

function mapOption(o: any): RowsCatalogOption | null {
  if (!o || !o.value) {
    return null;
  }
  return {
    value: String(o.value),
    label: String(o.label ?? o.value),
    label_en: o.label_en != null ? String(o.label_en) : null,
    label_fr: o.label_fr != null ? String(o.label_fr) : null,
    labelKey: o.labelKey
  };
}

/**
 * Internal storage code from a visible label (admin never types this).
 * Keeps accents stripped and stable for JSON keys / option values.
 */
export function slugifyRowsCode(text: string, fallback = 'campo'): string {
  const slug = String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return slug || fallback;
}

function uniqueSlug(base: string, used: Set<string>, fallback = 'campo'): string {
  let slug = slugifyRowsCode(base, fallback);
  if (!used.has(slug)) {
    used.add(slug);
    return slug;
  }
  let i = 2;
  while (used.has(`${slug}-${i}`)) {
    i += 1;
  }
  const next = `${slug}-${i}`;
  used.add(next);
  return next;
}

/**
 * Max rows = option count of unique-across-rows selects (e.g. one row per population).
 * Without a unique select, use a soft ceiling.
 */
export function computeMaxRows(fields: RepeatableFieldDef[] | null | undefined): number {
  const list = fields || [];
  const uniqueSelects = list.filter(f => f.type === 'select' && f.unique_across_rows);
  if (uniqueSelects.length) {
    const counts = uniqueSelects.map(
      f => (f.options || []).filter(o => String(o?.label || o?.value || '').trim()).length
    );
    const n = Math.min(...counts);
    return Math.max(1, Math.min(50, n || 1));
  }
  return 20;
}

/**
 * Fill missing field keys / option values from labels. Existing codes stay stable
 * so saved responses and show_when rules keep working when labels are edited.
 * Empty drafts (no label yet) are kept unless `dropEmpty` is true.
 */
export function ensureCatalogCodes(
  fields: RepeatableFieldDef[],
  opts?: { dropEmpty?: boolean }
): RepeatableFieldDef[] {
  const dropEmpty = !!opts?.dropEmpty;
  const usedKeys = new Set<string>();
  return fields
    .map(f => {
      const label = String(f.label || '').trim();
      let key = String(f.key || '').trim();
      if (!label && !key) {
        return dropEmpty ? null : { ...f, key: '', label: '' };
      }
      if (!key) {
        key = uniqueSlug(label, usedKeys);
      } else {
        usedKeys.add(key);
      }

      const next: RepeatableFieldDef = { ...f, key, label: label || key };

      if (next.type === 'select') {
        const usedVals = new Set<string>();
        next.options = (next.options || [])
          .map(o => {
            const optLabel = String(o.label || '').trim();
            let value = String(o.value || '').trim();
            if (!optLabel && !value) {
              return dropEmpty ? null : { ...o, value: '', label: '' };
            }
            if (!value) {
              value = uniqueSlug(optLabel, usedVals, 'opcion');
            } else {
              usedVals.add(value);
            }
            return { ...o, value, label: optLabel || value };
          })
          .filter(Boolean) as RowsCatalogOption[];
      }

      if (next.show_when?.field) {
        next.show_when = {
          field: String(next.show_when.field),
          equals: String(next.show_when.equals ?? '')
        };
      } else {
        next.show_when = null;
      }
      return next;
    })
    .filter(Boolean) as RepeatableFieldDef[];
}

function normalizeField(raw: any): RepeatableFieldDef | null {
  if (!raw) {
    return null;
  }
  const label = String(raw.label || raw.key || '').trim();
  const key = String(raw.key || '').trim();
  // Allow label-only drafts; ensureCatalogCodes fills keys/values on normalize.
  if (!key && !label) {
    return null;
  }
  const type: RepeatableFieldType = raw.type === 'text' ? 'text' : 'select';
  const field: RepeatableFieldDef = {
    key: key || '',
    label: label || key,
    label_en: raw.label_en != null ? String(raw.label_en) : null,
    label_fr: raw.label_fr != null ? String(raw.label_fr) : null,
    type,
    required: raw.required !== false,
    default_value:
      raw.default_value != null && raw.default_value !== ''
        ? String(raw.default_value)
        : null,
    unique_across_rows: !!raw.unique_across_rows
  };
  if (type === 'select') {
    field.options = (Array.isArray(raw.options) ? raw.options : [])
      .map((o: any) => {
        if (!o) {
          return null;
        }
        const optLabel = String(o.label || o.value || '').trim();
        const value = String(o.value || '').trim();
        if (!optLabel && !value) {
          return null;
        }
        return {
          value,
          label: optLabel || value,
          label_en: o.label_en != null ? String(o.label_en) : null,
          label_fr: o.label_fr != null ? String(o.label_fr) : null,
          labelKey: o.labelKey
        } as RowsCatalogOption;
      })
      .filter(Boolean) as RowsCatalogOption[];
  }
  if (raw.show_when && raw.show_when.field) {
    field.show_when = {
      field: String(raw.show_when.field),
      equals: String(raw.show_when.equals ?? '')
    };
  } else {
    field.show_when = null;
  }
  return field;
}

/** Convert legacy v1 populations/ranges catalog into fields[]. */
export function fieldsFromLegacyCatalog(raw: any): RepeatableFieldDef[] {
  const populations = (Array.isArray(raw?.populations) ? raw.populations : DEFAULT_POPULATIONS)
    .map(mapOption)
    .filter(Boolean) as RowsCatalogOption[];
  const ranges = (Array.isArray(raw?.ranges) ? raw.ranges : DEFAULT_RANGES)
    .map(mapOption)
    .filter(Boolean) as RowsCatalogOption[];
  const otherList = Array.isArray(raw?.require_other_text_for)
    ? raw.require_other_text_for.map(String)
    : [OTHER_POPULATION_VALUE];
  return buildDefaultVulnerabilityFields(populations, ranges, {
    rangeLabel: raw?.range_field_label,
    rangeLabelEn: raw?.range_field_label_en,
    rangeLabelFr: raw?.range_field_label_fr,
    defaultRange: raw?.default_range || 'no-determinado',
    otherEquals: otherList[0] || OTHER_POPULATION_VALUE
  });
}

/** Derive legacy mirrors from fields for SMS / pie charts. */
function legacyMirrorsFromFields(fields: RepeatableFieldDef[]): {
  populations: RowsCatalogOption[];
  ranges: RowsCatalogOption[];
  default_range: string;
  require_other_text_for: string[];
  range_field_label: string;
  range_field_label_en: string;
  range_field_label_fr: string;
} {
  const popField = fields.find(f => f.key === 'population') || fields.find(f => f.unique_across_rows);
  const rangeField = fields.find(f => f.key === 'range') || fields.find(f => f.type === 'select' && f !== popField);
  const otherField = fields.find(f => f.key === 'other_text') || fields.find(f => f.type === 'text' && f.show_when);
  return {
    populations: [...(popField?.options || [])],
    ranges: [...(rangeField?.options || [])],
    default_range: String(rangeField?.default_value || ''),
    require_other_text_for: otherField?.show_when?.equals
      ? [otherField.show_when.equals]
      : [],
    range_field_label: rangeField?.label || '',
    range_field_label_en: rangeField?.label_en || '',
    range_field_label_fr: rangeField?.label_fr || ''
  };
}

export function normalizeRowsCatalog(raw: any): RepeatableRowsCatalog {
  let fields: RepeatableFieldDef[] = [];
  if (Array.isArray(raw?.fields) && raw.fields.length) {
    fields = raw.fields.map(normalizeField).filter(Boolean) as RepeatableFieldDef[];
  }
  if (!fields.length) {
    fields = fieldsFromLegacyCatalog(raw || {});
  }
  fields = ensureCatalogCodes(fields, { dropEmpty: true });

  const max = computeMaxRows(fields);

  const mirrors = legacyMirrorsFromFields(fields);
  if (!mirrors.default_range && mirrors.ranges.length) {
    mirrors.default_range = mirrors.ranges[mirrors.ranges.length - 1].value;
  }

  return {
    version: 2,
    max_rows: max,
    fields,
    populations: mirrors.populations.length
      ? mirrors.populations
      : [...DEFAULT_POPULATIONS],
    ranges: mirrors.ranges.length ? mirrors.ranges : [...DEFAULT_RANGES],
    default_range: mirrors.default_range || 'no-determinado',
    require_other_text_for: mirrors.require_other_text_for.length
      ? mirrors.require_other_text_for
      : [OTHER_POPULATION_VALUE],
    range_field_label: mirrors.range_field_label,
    range_field_label_en: mirrors.range_field_label_en,
    range_field_label_fr: mirrors.range_field_label_fr
  };
}

export function catalogOptionLabel(
  option: RowsCatalogOption | undefined,
  lang?: string | null,
  fallback = ''
): string {
  if (!option) {
    return fallback;
  }
  const code = (lang || 'es').toLowerCase().slice(0, 2);
  if (code === 'en' && option.label_en) {
    return option.label_en;
  }
  if (code === 'fr' && option.label_fr) {
    return option.label_fr;
  }
  return option.label || option.value || fallback;
}

export function catalogFieldLabel(
  field: RepeatableFieldDef | undefined,
  lang?: string | null
): string {
  if (!field) {
    return '';
  }
  const code = (lang || 'es').toLowerCase().slice(0, 2);
  if (code === 'en' && field.label_en) {
    return field.label_en;
  }
  if (code === 'fr' && field.label_fr) {
    return field.label_fr;
  }
  return field.label || field.key;
}

/** @deprecated use catalogFieldLabel on the range field */
export function catalogRangeFieldLabel(
  catalog: RepeatableRowsCatalog,
  lang?: string | null
): string {
  const rangeField = (catalog.fields || []).find(f => f.key === 'range');
  if (rangeField) {
    return catalogFieldLabel(rangeField, lang);
  }
  const code = (lang || 'es').toLowerCase().slice(0, 2);
  if (code === 'en' && catalog.range_field_label_en) {
    return catalog.range_field_label_en;
  }
  if (code === 'fr' && catalog.range_field_label_fr) {
    return catalog.range_field_label_fr;
  }
  return catalog.range_field_label || '';
}

export function isFieldVisible(
  field: RepeatableFieldDef,
  row: VulnerabilityRow
): boolean {
  if (!field.show_when?.field) {
    return true;
  }
  return String(row?.[field.show_when.field] ?? '') === String(field.show_when.equals);
}

export function createBlankRow(catalog: RepeatableRowsCatalog): VulnerabilityRow {
  const row: VulnerabilityRow = {};
  for (const field of catalog.fields || []) {
    if (field.type === 'select') {
      row[field.key] = field.default_value || '';
    } else {
      row[field.key] = null;
    }
  }
  return row;
}

export function parseVulnerabilityRows(raw: unknown): VulnerabilityRow[] {
  if (raw == null || raw === '') {
    return [];
  }

  const isRowObject = (r: any): boolean =>
    !!r && typeof r === 'object' && !Array.isArray(r);

  if (Array.isArray(raw) && raw.length > 0 && raw.every(isRowObject)) {
    return (raw as any[]).map(row => ({ ...row }));
  }

  let payload: unknown = raw;

  if (Array.isArray(raw) && raw.length >= 1 && typeof raw[0] === 'string') {
    payload = raw[0];
  } else if (Array.isArray(raw) && raw.length === 1) {
    payload = raw[0];
  }

  if (Array.isArray(payload) && payload.length === 1 && Array.isArray(payload[0])) {
    payload = payload[0];
  }

  if (typeof payload === 'string') {
    let trimmed = payload.trim();
    if (!trimmed || trimmed === '[]') {
      return [];
    }
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))
    ) {
      trimmed = trimmed.slice(1, -1);
    }
    try {
      payload = JSON.parse(trimmed);
      if (typeof payload === 'string') {
        payload = JSON.parse(payload);
      }
    } catch {
      try {
        payload = JSON.parse(trimmed.replace(/\\"/g, '"').replace(/\\\\/g, '\\'));
      } catch {
        return [];
      }
    }
  }

  if (!Array.isArray(payload)) {
    if (isRowObject(payload)) {
      payload = [payload];
    } else {
      return [];
    }
  }

  return (payload as any[])
    .filter(isRowObject)
    .map((row: any) => {
      const out: VulnerabilityRow = {};
      Object.keys(row).forEach(k => {
        const v = row[k];
        out[k] = v == null ? null : String(v);
      });
      return out;
    });
}

export function encodeVulnerabilityRows(
  rows: VulnerabilityRow[],
  catalog?: RepeatableRowsCatalog
): string {
  const cat = catalog || DEFAULT_ROWS_CATALOG;
  const meaningful = pruneBlankVulnerabilityRows(rows, cat);
  return JSON.stringify(
    meaningful.map(r => {
      const out: VulnerabilityRow = {};
      for (const field of cat.fields || []) {
        if (!isFieldVisible(field, r)) {
          out[field.key] = null;
          continue;
        }
        const v = r[field.key];
        out[field.key] =
          v != null && String(v).trim() !== '' ? String(v).trim() : null;
      }
      // Keep unknown keys from legacy rows
      Object.keys(r || {}).forEach(k => {
        if (!(k in out)) {
          out[k] = r[k] ?? null;
        }
      });
      return out;
    })
  );
}

/** True when the row has no user-entered data (default-only values don't count). */
export function isBlankVulnerabilityRow(
  row: VulnerabilityRow,
  catalog?: RepeatableRowsCatalog
): boolean {
  const cat = catalog || DEFAULT_ROWS_CATALOG;
  for (const field of cat.fields || []) {
    if (!isFieldVisible(field, row)) {
      continue;
    }
    const val = String(row?.[field.key] ?? '').trim();
    if (!val) {
      continue;
    }
    if (
      field.type === 'select' &&
      field.default_value != null &&
      val === String(field.default_value)
    ) {
      continue;
    }
    return false;
  }
  return true;
}

export function pruneBlankVulnerabilityRows(
  rows: VulnerabilityRow[] | null | undefined,
  catalog?: RepeatableRowsCatalog
): VulnerabilityRow[] {
  const cat = catalog || DEFAULT_ROWS_CATALOG;
  return (rows || []).filter(r => !isBlankVulnerabilityRow(r, cat));
}

export function validateVulnerabilityRows(
  rows: VulnerabilityRow[],
  requireNonEmpty: boolean,
  catalog?: RepeatableRowsCatalog
): string | null {
  const cat = catalog || DEFAULT_ROWS_CATALOG;
  if (!rows?.length) {
    return requireNonEmpty ? 'VULN_ERR_ROWS_REQUIRED' : null;
  }
  if (rows.length > cat.max_rows) {
    return 'VULN_ERR_TOO_MANY';
  }

  const uniqueFields = (cat.fields || []).filter(f => f.unique_across_rows);
  const seenByField: Record<string, Set<string>> = {};
  uniqueFields.forEach(f => {
    seenByField[f.key] = new Set();
  });

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    for (const field of cat.fields || []) {
      if (!isFieldVisible(field, row)) {
        continue;
      }
      const raw = String(row[field.key] ?? '');
      const val = raw.trim();
      if (field.required !== false && !val) {
        return field.type === 'text'
          ? 'VULN_ERR_OTHER_REQUIRED'
          : 'VULN_ERR_POPULATION';
      }
      if (field.type === 'text' && raw) {
        if (raw.length > 150) {
          return 'FORM_MAX_LENGTH_150';
        }
        // Same charset as report Title field
        if (!/^[a-zA-Z0-9áéíóúüÁÉÍÓÚÜñÑ\s\-_.',;:]*$/.test(raw)) {
          return 'FORM_NO_SPECIAL_CHARACTER';
        }
      }
      if (field.type === 'select' && val) {
        const allowed = new Set((field.options || []).map(o => o.value));
        if (!allowed.has(val)) {
          return 'VULN_ERR_POPULATION';
        }
      }
      if (field.unique_across_rows && val) {
        if (seenByField[field.key].has(val)) {
          return 'VULN_ERR_DUPLICATE';
        }
        seenByField[field.key].add(val);
      }
    }
  }
  return null;
}

export function vulnerabilityPopulationLabel(
  population: string,
  translateOrLang?: ((key: string) => string) | string | null,
  otherText?: string | null,
  catalog?: RepeatableRowsCatalog
): string {
  const cat = catalog || DEFAULT_ROWS_CATALOG;
  const opt = cat.populations.find(p => p.value === population);
  let lang: string | null = null;
  if (typeof translateOrLang === 'string') {
    lang = translateOrLang;
  } else {
    lang =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('languageCode')
        : 'es';
  }
  let base = catalogOptionLabel(opt, lang, population);
  const otherValues = new Set(cat.require_other_text_for);
  if (otherValues.has(population) && (otherText || '').trim()) {
    // Show only the free-text content, not the catalog label prefix.
    return String(otherText).trim();
  }
  return base;
}

export function vulnerabilityRangeLabel(
  range: string,
  translateOrLang?: ((key: string) => string) | string | null,
  catalog?: RepeatableRowsCatalog
): string {
  const cat = catalog || DEFAULT_ROWS_CATALOG;
  const opt = cat.ranges.find(r => r.value === range);
  let lang: string | null = null;
  if (typeof translateOrLang === 'string') {
    lang = translateOrLang;
  } else {
    lang =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('languageCode')
        : 'es';
  }
  return catalogOptionLabel(opt, lang, range);
}

export function formatVulnerabilityRowsDisplay(
  raw: unknown,
  translateOrLang?: ((key: string) => string) | string | null,
  catalog?: RepeatableRowsCatalog
): string {
  const cat = catalog || DEFAULT_ROWS_CATALOG;
  const rows = parseVulnerabilityRows(raw);
  if (!rows.length) {
    return '';
  }
  let lang: string | null = null;
  if (typeof translateOrLang === 'string') {
    lang = translateOrLang;
  } else {
    lang =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('languageCode')
        : 'es';
  }
  return rows
    .map((r, idx) => {
      const parts: string[] = [];
      const otherValues = new Set(cat.require_other_text_for);
      const popVal = String(r.population ?? '').trim();
      const hasOtherText =
        otherValues.has(popVal) && String(r.other_text ?? '').trim() !== '';
      for (const field of cat.fields || []) {
        if (!isFieldVisible(field, r)) {
          continue;
        }
        const val = String(r[field.key] ?? '').trim();
        if (!val) {
          continue;
        }
        // When "Otros" has free text, skip the catalog option label.
        if (field.key === 'population' && hasOtherText) {
          continue;
        }
        if (field.type === 'select') {
          const opt = (field.options || []).find(o => o.value === val);
          parts.push(catalogOptionLabel(opt, lang, val));
        } else {
          parts.push(val);
        }
      }
      return `${idx + 1}. ${parts.join(' - ')}`;
    })
    .join(' | ');
}
